import api from '@/apis'
import { createLatestOnly } from '@/utils/latestOnly'
import { createUiSession } from '@/utils/uiSession'

// REQUEST OWNERSHIP — clicking a different parent menu starts a new request for the same panel.
const root_request = createLatestOnly()
const level2_request = createLatestOnly()
const level3_request = createLatestOnly()

// UI SESSION — is this panel still on screen, showing the subtree this request is about?
//
// A DIFFERENT question from the one above, and the difference is load-bearing here. MenuSetting's
// `level_1_menu_selected` watcher CLOSES both panels and then dispatches a new level-2 read only.
// Nothing ever claims `level3_request`, so an in-flight level-3 read for the subtree the operator
// just left was still "latest" and committed `visibility: true` — RE-OPENING a panel they had
// closed, on the old subtree, next to the new card's children. De-selecting the card dispatches
// nothing at all, so ownership cannot answer for level 2 either, and `editRouteDialog::deleteRoute`
// closing the child panel of a route it removed is the same shape a third time.
const level2_panel = createUiSession()
const level3_panel = createUiSession()

export default {
  state: {
    service_type_list: [],
    level_1_menu_data: [],
    level_2_menu_data: {
      visibility: false,
      route_id: null,
      breadcrumbs: [
        {
          text: 'Menu',
          disabled: true
        },
        {
          text: 'General Enquiry',
          disabled: false
        }
      ],
      headers: [
        { text: '#', value: 'index', sortable: false },
        { text: 'Chinese', value: 'name', sortable: false },
        { text: 'CN', value: 'name_cn', sortable: false },
        { text: 'English', value: 'name_en', sortable: false },
        { text: 'Queue', value: 'queue', width: 100, sortable: false },
        { text: 'Edit', value: 'edit', sortable: false }
      ],
      items: []
    },
    level_3_menu_data: {
      visibility: false,
      route_id: null,
      title: null,
      headers: [
        { text: 'Chinese', value: 'name', sortable: false },
        { text: 'CN', value: 'name_cn', sortable: false },
        { text: 'English', value: 'name_en', sortable: false },
        { text: 'Queue', value: 'queue', sortable: false },
        { text: 'Edit', value: 'edit', sortable: false }
      ],
      items: []
    }
  },
  mutations: {
    SET_MENU_SETTING_LEVEL_1_MENU(state, payload) {
      state.level_1_menu_data = payload
    },
    // WHICH PARENT EACH PANEL IS ABOUT — set when a read STARTS. Writing it only on commit (below)
    // made it lag a round trip, so a route save could pass the "is my parent still on screen" check
    // while the operator's newer panel read was still in flight, and then supersede it. Same defect
    // as accountInfoCard's `team_id`; this is the device that one was copied from.
    SET_MENU_SETTING_LEVEL_2_TARGET(state, payload) {
      state.level_2_menu_data.route_id = payload
    },
    SET_MENU_SETTING_LEVEL_3_TARGET(state, payload) {
      state.level_3_menu_data.route_id = payload
    },
    SET_MENU_SETTING_LEVEL_2_MENU(state, payload) {
      state.level_2_menu_data.visibility = payload.visibility
      state.level_2_menu_data.breadcrumbs[1].text = payload.title
      state.level_2_menu_data.items = payload.items
      // WHICH parent this panel is showing. A route mutation reconciles the menu only when the
      // panel on screen is the one it changed — otherwise re-fetching would repaint the operator's
      // current panel with a different parent's children.
      state.level_2_menu_data.route_id = payload.route_id
    },
    // A UI ACT — the operator switched or de-selected the level-1 card. The paint above is NOT one:
    // it is a response, and a response must never end anyone's session.
    SET_MENU_SETTING_LEVEL_2_MENU_VISIBILITY(state, payload) {
      state.level_2_menu_data.visibility = payload
      level2_panel.transition()
    },
    SET_MENU_SETTING_LEVEL_3_MENU(state, payload) {
      state.level_3_menu_data.visibility = payload.visibility
      state.level_3_menu_data.title = payload.title
      state.level_3_menu_data.items = payload.items
      state.level_3_menu_data.route_id = payload.route_id
    },
    // A UI ACT — the operator moved off this subtree, or a route delete removed the route whose
    // children this panel lists.
    SET_MENU_SETTING_LEVEL_3_MENU_VISIBILITY(state, payload) {
      state.level_3_menu_data.visibility = payload
      level3_panel.transition()
    },
    SET_MENU_SETTING_SERVICE_TYPE_LIST(state, payload) {
      let service_type_list = JSON.parse(JSON.stringify(payload))
      service_type_list.unshift({ service_type_id: null, service_type: 'None', prefix: 'None' })
      state.service_type_list = service_type_list
    }
  },
  actions: {
    async fetchRootLevelRoute({ commit }) {
      const isLatest = root_request()
      try {
        const response = await api.getRootRoutes()
        // A newer request of this kind owns the view now — see utils/latestOnly.js.
        if (!isLatest()) return
        if (response.data.success) {
          commit('SET_MENU_SETTING_LEVEL_1_MENU', response.data.data.child)
        }
      } catch (error) {
        if (!isLatest()) return
        console.log('Init Root Level Route Error')
      }
    },
    async fetchLevel2Route({ commit }, payload) {
      const isLatest = level2_request()
      const samePanel = level2_panel.current()
      commit('SET_MENU_SETTING_LEVEL_2_TARGET', payload)
      try {
        const response = await api.getChildRoutes(payload)
        // TWO independent reasons to drop a READ, and only one of them is "someone newer asked":
        //   !isLatest()   a newer read of this kind owns the panel — see utils/latestOnly.js
        //   !samePanel()  the panel itself is gone. Nothing newer was requested, so ownership says
        //                 nothing at all; committing would re-open a panel the operator closed.
        if (!isLatest() || !samePanel()) return
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: 'Loading route...', loading: true })
        if (response.data.success) {
          const route = response.data.data
          commit('SET_MENU_SETTING_LEVEL_2_MENU', {
            visibility: true,
            title: route.parent.name,
            items: route.child,
            route_id: payload
          })
          commit('SET_SNACKBAR', { status: false })
        } else {
          commit('SET_SNACKBAR', { status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '2000' })
        }
      } catch (error) {
        if (!isLatest() || !samePanel()) return
        commit('SET_SNACKBAR', { status: true, color: 'error', text: 'Server Error', icon: 'close', timeout: '2000' })
      }
    },
    async fetchLevel3Route({ commit }, payload) {
      const isLatest = level3_request()
      const samePanel = level3_panel.current()
      commit('SET_MENU_SETTING_LEVEL_3_TARGET', payload)
      try {
        const response = await api.getChildRoutes(payload)
        // See fetchLevel2Route: ownership and the panel's own session are different questions.
        if (!isLatest() || !samePanel()) return
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: 'Loading route...', loading: true })
        if (response.data.success) {
          const route = response.data.data
          commit('SET_MENU_SETTING_LEVEL_3_MENU', {
            visibility: true,
            title: route.parent.name,
            items: route.child,
            route_id: payload
          })
          commit('SET_SNACKBAR', { status: false })
        } else {
          commit('SET_SNACKBAR', { status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '2000' })
        }
      } catch (error) {
        if (!isLatest() || !samePanel()) return
        commit('SET_SNACKBAR', { status: true, color: 'error', text: 'Server Error', icon: 'close', timeout: '2000' })
      }
    }
  }
}
